import React, { useEffect, useState } from "react";
import { FiAlertCircle, FiClock, FiMinus, FiPlus, FiRefreshCw, FiZap } from "react-icons/fi";
import { ErrorBox, OrgModal, PrimaryButton, SecondaryButton } from "../organisation/OrgModal";
import PlanCard from "./PlanCard";
import PlanComparison from "./PlanComparison";
import { METRIC_ICONS, Skeleton } from "./subscriptionUi";
import usePublicPlans from "../../hooks/usePublicPlans";
import useResetOnReturn from "../../hooks/useResetOnReturn";
import {
  apiErrorCode,
  apiErrorMessage,
  featureLabel,
  isShownFeature,
  formatCents,
  formatLimit,
  isUpgradeFrom,
  limitPhrase,
  limitChanges,
  METRICS,
  monthlyPrice,
  planOf,
  planState,
  PLAN_STATE,
  rememberCheckoutBaseline,
  startCheckout,
} from "../../utils/subscriptionData";

const TITLES = { buy: "Choose your plan", upgrade: "Choose your plan", change: "Change your plan" };

const CHECKOUT_ERRORS = {
  PLAN_NOT_AVAILABLE: "That plan can't be bought online right now. Pick another plan.",
  ALREADY_ON_PLAN: "You're already on that plan.",
  BILLING_NOT_CONFIGURED: "Payments are unavailable right now. Please try again later.",
};

const payingNow = (plan) =>
  plan?.source === "stripe" && [PLAN_STATE.ACTIVE, PLAN_STATE.PAST_DUE, PLAN_STATE.ENDING].includes(planState(plan));

// What the free trial includes, written from the organisation's own trial limits
const TrialBanner = ({ plan }) => (
  <div className="flex items-center gap-4 rounded-2xl bg-[#2252a5] p-4 text-white">
    <span className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15" aria-hidden="true">
      <FiClock className="h-5 w-5" />
    </span>
    <div className="text-sm">
      <p className="text-base font-semibold">You're on a free trial</p>
      <p className="text-white/90">
        It includes {METRICS.filter((metric) => metric.key in (plan.limits || {})).map((metric) => limitPhrase(plan, metric.key)).join(", ")}. Choose a plan when you're
        ready.
      </p>
    </div>
  </div>
);

// Plan picker for first purchase, upgrade and change. Cards come straight from GET /plans; the full
// comparison table is one click away. Custom-priced (contact sales) plans aren't offered here.
const PlanChangeModal = ({ isOpen, mode, initialPlanId, plan, onClose }) => {
  const { plans: allPlans, loading, error, retry } = usePublicPlans();
  const plans = allPlans.filter((item) => !item.contact_sales);
  const [target, setTarget] = useState(null);
  const [table, setTable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  // Back from Stripe Checkout: the button must not stay on "Opening checkout..."
  useResetOnReturn(() => setBusy(false));

  useEffect(() => {
    if (isOpen) {
      setTarget(null);
      setTable(false);
      setProblem("");
      setBusy(false);
    }
  }, [isOpen, mode, initialPlanId]);

  // A plan card opened the picker already pointing at one plan: go straight to its summary
  useEffect(() => {
    if (!isOpen || !initialPlanId) return;
    const wanted = plans.find((item) => item.plan_id === initialPlanId);
    if (wanted) setTarget(wanted);
    // plans is derived from allPlans each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialPlanId, allPlans]);

  const current = planOf(plan);
  const active = plan?.active !== false;
  const onTrial = planState(plan) === PLAN_STATE.TRIAL;
  const currentId = active && !current?.is_trial ? current?.plan_id : null;
  const currentPublic = currentId ? plans.find((item) => item.plan_id === currentId) || null : null;

  const choose = async () => {
    setBusy(true);
    setProblem("");
    try {
      const { checkout_url: url } = await startCheckout(target.plan_id);
      if (!url) throw new Error("No checkout address returned");
      rememberCheckoutBaseline();
      window.location.assign(url);
    } catch (err) {
      setProblem(CHECKOUT_ERRORS[apiErrorCode(err)] || apiErrorMessage(err, "Could not start checkout. Try again."));
      setBusy(false);
    }
  };

  const actionFor = (item) => {
    if (item.plan_id === currentId) {
      return <span className="flex h-10 w-full items-center justify-center rounded-lg border bg-gray-50 text-sm font-medium text-accent-gray-light">Your current plan</span>;
    }
    const upgrade = currentPublic ? isUpgradeFrom(currentPublic, item) : true;
    const Button = upgrade ? PrimaryButton : SecondaryButton;
    return (
      <Button fullWidth onClick={() => setTarget(item)} sx={{ height: "2.5rem" }}>
        {currentPublic && !upgrade ? "Switch to" : "Choose"} {item.name}
      </Button>
    );
  };

  // Everything below is read from the plans, so the summary stays right when the catalog changes
  const summary = () => {
    const lost = currentPublic ? Object.keys(currentPublic.features || {}).filter((key) => isShownFeature(key) && currentPublic.features[key] && !target.features?.[key]) : [];
    const gained = Object.keys(target.features || {}).filter((key) => isShownFeature(key) && target.features[key] && !currentPublic?.features?.[key]);
    const changes = limitChanges(current?.base_limits, target.limits);
    // null means unlimited, which is the highest value
    const rank = (value) => (value === null ? Infinity : value ?? 0);
    const notes = [
      { icon: FiZap, text: "Your new plan starts as soon as Stripe confirms the payment." },
      { icon: FiRefreshCw, text: "Usage counters for the new plan start at 0." },
      ...(payingNow(plan)
        ? [
            { icon: FiAlertCircle, text: "There's no refund for the rest of your current plan." },
            { icon: FiRefreshCw, text: "Extra seats you've added move to the new plan at its add-on price." },
          ]
        : []),
    ];
    const price = monthlyPrice(target);
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 rounded-xl bg-gradient-to-br from-[#2252a5] to-[#16397a] px-4 py-2.5 text-white">
          <div className="min-w-0">
            <p className="text-lg font-bold leading-tight">{target.name}</p>
            <p className="text-[11px] text-white/80">Billed monthly through Stripe Checkout</p>
          </div>
          <p className="shrink-0 text-right leading-tight">
            <span className="text-2xl font-bold">{price ? formatCents(price.amount_cents, price.currency) : "—"}</span>
            <span className="text-xs text-white/80"> / month</span>
          </p>
        </div>

        {changes.length > 0 && (
          <div className="overflow-hidden rounded-xl border">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">What changes with {target.name}</caption>
              <thead>
                <tr className="bg-[#f3f6fb] text-xs text-accent-gray-light">
                  <th scope="col" className="px-3 py-2 text-left font-medium">What you get</th>
                  <th scope="col" className="px-2 py-2 text-right font-medium">Now</th>
                  <th scope="col" className="px-2 py-2 text-right font-medium">With {target.name}</th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">Change</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((row) => {
                  const Icon = METRIC_ICONS[row.key];
                  const up = rank(row.to) > rank(row.from);
                  const down = rank(row.to) < rank(row.from);
                  // A number difference when both sides are numbers; otherwise just more or less
                  const both = typeof row.from === "number" && typeof row.to === "number";
                  const delta = both ? `${row.to > row.from ? "+" : "−"}${formatLimit(Math.abs(row.to - row.from))}` : up ? "More" : down ? "Less" : "";
                  return (
                    <tr key={row.key} className="border-t">
                      <th scope="row" className="px-3 py-2 text-left font-medium">
                        <span className="flex items-center gap-2">
                          {Icon && <Icon className="h-4 w-4 shrink-0 text-accent-blue" aria-hidden="true" />}
                          {row.label}
                        </span>
                      </th>
                      <td className="px-2 py-2 text-right tabular-nums text-accent-gray-light">{row.from === undefined ? "—" : formatLimit(row.from)}</td>
                      <td className="px-2 py-2 text-right font-bold tabular-nums">{formatLimit(row.to)}</td>
                      <td className="px-3 py-2 text-right">
                        {delta && (
                          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${up ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{delta}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {(gained.length > 0 || lost.length > 0) && (
          <div className="flex flex-wrap gap-2" aria-label="Feature changes">
            {gained.map((key) => (
              <span key={key} className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-800">
                <FiPlus className="h-3 w-3" aria-hidden="true" />
                <span className="sr-only">You gain: </span>
                {featureLabel(key)}
              </span>
            ))}
            {lost.map((key) => (
              <span key={key} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
                <FiMinus className="h-3 w-3" aria-hidden="true" />
                <span className="sr-only">You lose: </span>
                {featureLabel(key)}
              </span>
            ))}
          </div>
        )}

        <ul className="space-y-1.5 rounded-xl bg-[#f3f6fb] p-3 text-sm">
          {notes.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-2">
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent-blue" aria-hidden="true" />
              <span>{text}</span>
            </li>
          ))}
        </ul>
        <ErrorBox>{problem}</ErrorBox>
      </div>
    );
  };

  const body = () => {
    if (target) return summary();
    if (loading) {
      return (
        <ul role="status" aria-label="Loading plans" className="grid gap-4 sm:grid-cols-2">
          {[0, 1, 2].map((item) => (
            <li key={item}>
              <Skeleton className="h-96 w-full" />
            </li>
          ))}
        </ul>
      );
    }
    if (error) {
      return (
        <div role="alert" className="p-4 border border-red-300 bg-red-50 text-red-900 rounded flex flex-wrap items-center justify-between gap-3">
          <span>We couldn't load the plans. Check your connection and try again.</span>
          <SecondaryButton onClick={retry}>Try again</SecondaryButton>
        </div>
      );
    }
    if (!plans.length) return <p className="text-sm text-accent-gray-light">No plans are available right now. Please check back soon.</p>;
    if (table) {
      return <PlanComparison plans={plans} currentPlan={active ? current : null} caption="Plan comparison" />;
    }
    return (
      <>
        {onTrial && <TrialBanner plan={plan} />}
        <ul className="grid gap-4 sm:grid-cols-2 items-stretch">
          {plans.map((item) => (
            <PlanCard key={item.plan_id} plan={item} isCurrent={item.plan_id === currentId} action={actionFor(item)} />
          ))}
        </ul>
      </>
    );
  };

  return (
    <OrgModal
      isOpen={isOpen}
      onClose={busy ? () => {} : onClose}
      titleId="plan-change-title"
      title={target ? "Confirm your plan" : table ? "Compare all features" : TITLES[mode] || TITLES.buy}
      maxWidth={target ? "max-w-xl" : "max-w-47rem"}
      footer={
        target ? (
          <>
            <SecondaryButton onClick={() => setTarget(null)} disabled={busy}>
              Back
            </SecondaryButton>
            <PrimaryButton onClick={choose} disabled={busy}>
              {busy ? "Opening checkout..." : "Continue to checkout"}
            </PrimaryButton>
          </>
        ) : (
          <>
            {plans.length > 0 && !loading && <SecondaryButton onClick={() => setTable(!table)}>{table ? "Back to plans" : "Compare all features"}</SecondaryButton>}
          </>
        )
      }
    >
      {!target && !table && !loading && !error && plans.length > 0 && (
        <p className="text-sm text-accent-gray-light -mt-2">Prices are per month. Add-ons can be bought on top of any plan once you're subscribed.</p>
      )}
      {body()}
    </OrgModal>
  );
};

export default PlanChangeModal;
